import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { GraphDevice, stacklightUsesWs2812 } from '../flow/graph';
import { ConfigSchema, deviceItemTypes } from '../schema/schema';
import { SchemaObjectForm } from '../components/SchemaForm';
import StripRefsField from './StripRefsField';
import ChannelRefsField from './ChannelRefsField';
import { useI18n } from '../i18n';

interface DeviceInspectorProps {
  device: GraphDevice;
  schema: ConfigSchema;
  existingDevices?: GraphDevice[];
  disabled?: boolean;
  onEditSignals?: () => void;
  onChange: (next: GraphDevice) => void;
}

const DeviceInspector: React.FC<DeviceInspectorProps> = ({
  device,
  schema,
  existingDevices = [],
  disabled,
  onEditSignals,
  onChange,
}) => {
  const { t } = useI18n();
  const spec = deviceItemTypes(schema)[String(device.type)];
  const formFields = useMemo(() => {
    const fields = { ...(spec?.fields || {}) };
    delete fields.strips;
    delete fields.channels;
    delete fields.count;
    return fields;
  }, [spec]);
  if (!spec) {
    return <Typography color="text.secondary">{t('deviceConfig.unknownType', { type: String(device.type) })}</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <SchemaObjectForm
        groupId="instance"
        fields={formFields}
        values={device}
        disabled={disabled}
        onChange={(next) => {
          const merged: GraphDevice = { ...device, ...next, type: device.type };
          if (device.type === 'gpio_relay' && Array.isArray(merged.pins)) {
            const pins = (merged.pins as unknown[])
              .map((n) => Number(n))
              .filter((n) => Number.isFinite(n))
              .slice(0, 4);
            merged.pins = pins;
            merged.count = pins.length;
          }
          onChange(merged);
        }}
      />
      {device.type === 'stacklight' && stacklightUsesWs2812(device) && (
        <StripRefsField
          device={device}
          existing={existingDevices}
          disabled={disabled}
          onChange={(strips) => onChange({ ...device, strips })}
        />
      )}
      {device.type === 'stacklight' && !stacklightUsesWs2812(device) && (
        <ChannelRefsField
          device={device}
          existing={existingDevices}
          disabled={disabled}
          onChange={(channels) => onChange({ ...device, channels })}
        />
      )}
      {device.type === 'stacklight' && onEditSignals && (
        <Button size="small" variant="outlined" disabled={disabled} onClick={onEditSignals}>
          {t('deviceConfig.editSignalsConfig')}
        </Button>
      )}
    </Box>
  );
};

export default DeviceInspector;
